import {
  containsHighRiskAuthCredential,
  isSensitiveAuthTelemetryUrl,
  sanitizeSensitiveUrl,
} from '@/utils/urlPrivacy';
import { normalizeMetaAdObjectId } from '../../supabase/functions/_shared/funnelAttribution';
import {
  APP_SHORT_LINK_KIND,
  FIXED_SHORT_LINK_MEDIUM,
  buildAppStoreShortLinkUrl,
  getFixedShortLinkKind,
  getFixedShortLinkPath,
  PROMPT_SHORT_SHARE_KIND,
  buildPromptPageTargetPath,
  hasDotPathSegment,
  isPromptPagePath,
} from '../../api/_lib/shortShareRules';

export type ReferrerSource = 'external' | 'same_origin' | 'empty';
export type AppsFlyerAttributionStatus = 'Organic' | 'Non-organic';

export interface AcquisitionContext {
  captured_at: string;
  last_touched_at: string;
  source_url: string | null;
  landing_path: string | null;
  referrer: string | null;
  referrer_source?: ReferrerSource | null;
  af_status?: string | null;
  network: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  /** utm_id from the ad URL (Meta's URL builder puts the campaign id here). */
  utm_id?: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  fbclid: string | null;
  ttclid: string | null;
  msclkid: string | null;
  campaign: string | null;
  ad_group: string | null;
  ad: string | null;
  creative: string | null;
  keyword: string | null;
  fbc: string | null;
  /**
   * Meta campaign, ad set and ad ids of the latest Meta ad click (digits
   * only). Reset by every new fbclid so they always describe that click.
   */
  meta_campaign_id?: string | null;
  meta_adset_id?: string | null;
  meta_ad_id?: string | null;
  appsflyer_media_source?: string | null;
  appsflyer_campaign_id?: string | null;
  appsflyer_adset_id?: string | null;
  appsflyer_ad_id?: string | null;
  appsflyer_channel?: string | null;
  appsflyer_site_id?: string | null;
  deep_link_value?: string | null;
  deep_link_sub1?: string | null;
  deep_link_sub2?: string | null;
  deep_link_sub3?: string | null;
  deep_link_sub4?: string | null;
  deep_link_sub5?: string | null;
}

export interface RevenueCatAttributionContext {
  mediaSource: string | null;
  campaign: string | null;
  adGroup: string | null;
  ad: string | null;
  creative: string | null;
  keyword: string | null;
}

interface Ga4AttributionSource {
  network?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_term?: string | null;
  utm_content?: string | null;
  fbclid?: string | null;
}

interface NormalizedGa4Attribution {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  paidSocialPlacement: string | null;
  wasNormalized: boolean;
}

const STORAGE_KEY = 'jp:acquisition-context';
const FIRST_TOUCH_KEY = 'jp:acquisition-first-touch';
const SESSION_TOUCH_KEY = 'jp:acquisition-session-touch';
const SESSION_TTL_MS = 30 * 60 * 1000;
const TIKTOK_CLICK_ID_COOKIE = 'ttclid';
const TTL_MS = 1000 * 60 * 60 * 24 * 30;
const GA4_PAID_SOCIAL_MEDIUM = 'paid_social';
const PAID_MEDIUM_REGEX = /^(.*cp.*|ppc|retargeting|paid.*)$/i;
const META_SOURCE_VALUES = new Set([
  'fb',
  'facebook',
  'facebook_ads',
  'facebookads',
  'ig',
  'instagram',
  'instagram_ads',
  'instagramads',
  'meta',
  'meta_ads',
  'metaads',
  'th',
  'threads',
]);
const META_PLACEMENT_MEDIUM_REGEX =
  /^(facebook|fb|instagram|ig|threads|th)[_\-\s].+|.+[_\-\s](feed|feeds|reel|reels|story|stories)$/i;
const SHORT_SHARE_PREFIXES = new Set(['r', 'ref']);
const SHORT_SHARE_KINDS = new Set(['p', 'e', 'j']);
const SEARCH_REFERRER_HOSTS: Array<{ source: string; hostPattern: RegExp }> = [
  { source: 'google', hostPattern: /(^|\.)google\./i },
  { source: 'bing', hostPattern: /(^|\.)bing\.com$/i },
  { source: 'duckduckgo', hostPattern: /(^|\.)duckduckgo\.com$/i },
  { source: 'yahoo', hostPattern: /(^|\.)yahoo\.com$/i },
  { source: 'ecosia', hostPattern: /(^|\.)ecosia\.org$/i },
  { source: 'brave', hostPattern: /(^|\.)search\.brave\.com$/i },
];

const TRACKING_PARAM_NAMES = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'ttclid',
  'msclkid',
  'campaign',
  'campaign_id',
  'adgroup',
  'ad_group',
  'adgroup_id',
  'adset',
  'adset_id',
  'ad',
  'ad_id',
  'creative',
  'creative_id',
  'keyword',
  'af_keywords',
  'media_source',
  'pid',
  'c',
  'af_c_id',
  'af_adset',
  'af_adset_id',
  'af_ad',
  'af_ad_id',
  'af_channel',
  'af_siteid',
  'af_sub1',
  'af_sub2',
  'af_sub3',
  'af_sub4',
  'af_sub5',
  'deep_link_value',
  'deep_link_sub1',
  'deep_link_sub2',
  'deep_link_sub3',
  'deep_link_sub4',
  'deep_link_sub5',
  'af_dp',
] as const;

const LINK_SOURCE_PARAM_NAMES: ReadonlySet<string> = new Set(
  TRACKING_PARAM_NAMES.filter((key) => !key.startsWith('deep_link_') && key !== 'af_dp'),
);

export function normalizeAppsFlyerAttributionStatus(value?: string | null): AppsFlyerAttributionStatus | null {
  const normalized = compact(value)
    ?.toLowerCase()
    .replace(/[\s_-]+/g, '');
  if (normalized === 'organic') return 'Organic';
  if (normalized === 'nonorganic') return 'Non-organic';
  return null;
}

function readStorage(key = STORAGE_KEY, session = false): AcquisitionContext | null {
  if (typeof window === 'undefined') return null;

  try {
    const raw = (session ? window.sessionStorage : window.localStorage).getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AcquisitionContext;
    const lastTouchedAt = Date.parse(parsed.last_touched_at);
    if (!Number.isFinite(lastTouchedAt) || Date.now() - lastTouchedAt > (session ? SESSION_TTL_MS : TTL_MS)) {
      (session ? window.sessionStorage : window.localStorage).removeItem(key);
      return null;
    }
    if (
      (parsed.source_url && containsHighRiskAuthCredential(parsed.source_url)) ||
      (parsed.landing_path && containsHighRiskAuthCredential(parsed.landing_path))
    ) {
      (session ? window.sessionStorage : window.localStorage).removeItem(key);
      return null;
    }

    const sanitized = {
      ...parsed,
      source_url: parsed.source_url ? sanitizeSensitiveUrl(parsed.source_url, 'absolute') : null,
      landing_path: parsed.landing_path ? sanitizeSensitiveUrl(parsed.landing_path, 'path') : null,
      // Deep-link callbacks use values such as `not_found` and `failure` in
      // their generic `status` field. Those are transport outcomes, not
      // AppsFlyer attribution classifications, and must never poison the
      // stored first-touch status.
      af_status: normalizeAppsFlyerAttributionStatus(parsed.af_status),
    };
    if (
      sanitized.source_url !== parsed.source_url ||
      sanitized.landing_path !== parsed.landing_path ||
      sanitized.af_status !== parsed.af_status
    ) {
      (session ? window.sessionStorage : window.localStorage).setItem(key, JSON.stringify(sanitized));
    }
    return sanitized;
  } catch {
    return null;
  }
}

const FIRST_TOUCH_ENRICHMENT_FIELDS = [
    'gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid',
    'network', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'utm_id', 'af_status', 'campaign', 'ad_group', 'ad', 'creative', 'keyword',
    'meta_campaign_id', 'meta_adset_id', 'meta_ad_id', 'appsflyer_media_source',
    'appsflyer_campaign_id', 'appsflyer_adset_id', 'appsflyer_ad_id', 'appsflyer_channel',
    'appsflyer_site_id', 'deep_link_value', 'deep_link_sub1', 'deep_link_sub2',
    'deep_link_sub3', 'deep_link_sub4', 'deep_link_sub5',
] as const;

function canEnrichFirstTouch(first: AcquisitionContext, next: AcquisitionContext): boolean {
  const clickFields = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'ttclid', 'msclkid'] as const;
  const matchingClick = clickFields.some((key) => first[key] && first[key] === next[key]);
  // Native data may arrive first with only routing or install classification.
  // Retain that supported native enrichment path without treating a web entry
  // or an Organic-to-Non-organic transition as the same install.
  const matchingThinNativeInstall = !first.utm_source && !first.network &&
    (!first.source_url || first.source_url.startsWith('appsflyer:')) &&
    next.source_url?.startsWith('appsflyer:') &&
    (!first.af_status || first.af_status === next.af_status);
  if (!matchingClick && !matchingThinNativeInstall) return false;
  if (FIRST_TOUCH_ENRICHMENT_FIELDS.some((key) => first[key] && next[key] && first[key] !== next[key])) return false;
  if (first.fbc && next.fbc && fbclidFromFbc(first.fbc) !== fbclidFromFbc(next.fbc)) return false;
  // AppsFlyer adds attribution query fields as data arrives. The original
  // landing stays fixed, and a different origin/path cannot enrich it.
  try {
    if (first.source_url && next.source_url) {
      const previousUrl = new URL(first.source_url);
      const nextUrl = new URL(next.source_url);
      for (const key of [...TRACKING_PARAM_NAMES, 'af_status']) {
        const previousValue = previousUrl.searchParams.get(key);
        const nextValue = nextUrl.searchParams.get(key);
        if (previousValue && nextValue && previousValue !== nextValue) return false;
        previousUrl.searchParams.delete(key);
        nextUrl.searchParams.delete(key);
      }
      // These keys describe SDK callback stages, not a web landing query.
      if ([previousUrl, nextUrl].every((url) => url.protocol === 'appsflyer:' && url.host === 'native-attribution')) {
        for (const keys of [['install_time'], ['click_time', 'af_click_time']]) {
          const previousTime = readParam(previousUrl.searchParams, ...keys);
          const nextTime = readParam(nextUrl.searchParams, ...keys);
          if (previousTime && nextTime && previousTime !== nextTime) return false;
        }
        for (const key of ['source', 'is_deferred', 'is_first_launch', 'install_time', 'click_time', 'af_click_time']) {
          previousUrl.searchParams.delete(key);
          nextUrl.searchParams.delete(key);
        }
      }
      previousUrl.searchParams.sort();
      nextUrl.searchParams.sort();
      if (previousUrl.protocol !== nextUrl.protocol || previousUrl.host !== nextUrl.host ||
          previousUrl.pathname !== nextUrl.pathname || previousUrl.hash !== nextUrl.hash ||
          previousUrl.search !== nextUrl.search) return false;
    }
  } catch {
    return false;
  }
  return true;
}

function storeTouchSnapshots(context: AcquisitionContext): void {
  if (typeof window === "undefined") return;
  try {
    // Existing latest-touch storage is never relabelled as historical first touch.
    const first = readStorage(FIRST_TOUCH_KEY);
    if (!first) {
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(context));
    } else if (canEnrichFirstTouch(first, context)) {
      // Matching-click retries may fill gaps without changing recorded first
      // values, including its original landing, referrer and timestamps.
      const enriched = { ...first };
      for (const key of [...FIRST_TOUCH_ENRICHMENT_FIELDS, 'fbc'] as const) {
        const value = context[key];
        if (!enriched[key] && value) enriched[key] = value;
      }
      window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(enriched));
    }
    const session = readStorage(SESSION_TOUCH_KEY, true);
    window.sessionStorage.setItem(
      SESSION_TOUCH_KEY,
      JSON.stringify(
        !session
          ? context
          : { ...session, last_touched_at: context.last_touched_at },
      ),
    );
  } catch {
    // Attribution remains optional when browser storage is unavailable.
  }
}

function writeStorage(context: AcquisitionContext): AcquisitionContext {
  if (typeof window === 'undefined') return context;

  storeTouchSnapshots(context);
  persistTikTokClickId(context.ttclid);

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch {
    // Ignore storage failures.
  }

  return context;
}

function getSharedCookieDomain(): string | null {
  if (typeof window === 'undefined') return null;

  const { hostname } = window.location;
  if (hostname === 'journalparty.com' || hostname.endsWith('.journalparty.com')) {
    return '.journalparty.com';
  }

  return null;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === 'undefined') return;

  const parts = [`${name}=${encodeURIComponent(value)}`, 'path=/', 'SameSite=Lax', `max-age=${maxAgeSeconds}`];
  const domain = getSharedCookieDomain();
  if (domain) parts.push(`domain=${domain}`);
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

function persistTikTokClickId(ttclid: string | null): void {
  const normalized = compact(ttclid);
  if (!normalized) return;

  writeCookie(TIKTOK_CLICK_ID_COOKIE, normalized, Math.floor(TTL_MS / 1000));
}

function readParam(params: URLSearchParams, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = params.get(key);
    if (value && value.trim()) return value.trim();
  }
  return null;
}

function inferNetwork(params: URLSearchParams): string | null {
  if (params.get('utm_source')) return params.get('utm_source')!.trim().toLowerCase();
  if (params.get('media_source')) return params.get('media_source')!.trim().toLowerCase();
  if (params.get('pid')) return params.get('pid')!.trim().toLowerCase();
  if (params.get('fbclid')) return 'meta';
  if (params.get('gclid') || params.get('gbraid') || params.get('wbraid')) return 'google';
  if (params.get('ttclid')) return 'tiktok';
  if (params.get('msclkid')) return 'microsoft';
  return null;
}

function hasTrackingParams(params: URLSearchParams): boolean {
  return TRACKING_PARAM_NAMES.some((key) => {
    const value = params.get(key);
    return typeof value === 'string' && value.trim().length > 0;
  });
}

/** The fbclid part of an `fb.<index>.<creationTimeMs>.<fbclid>` value. */
export function fbclidFromFbc(fbc?: string | null): string | null {
  const match = compact(fbc)?.match(/^fb\.\d+\.\d+\.(.+)$/);
  return match ? match[1] : null;
}

/**
 * Meta campaign, ad set and ad ids for a Meta ad click. Read from explicit
 * id parameters first, then from the utm template the plan sets for Meta web
 * ads (utm_id or utm_campaign = campaign id, utm_term = ad set id,
 * utm_content = ad id). Only all-digit values count, and only on a visit
 * that came from Meta, so Google or TikTok parameters never land here.
 */
function readMetaAdObjectIds(params: URLSearchParams): {
  campaignId: string | null;
  adsetId: string | null;
  adId: string | null;
} | null {
  const fromMeta = Boolean(readParam(params, 'fbclid')) || isMetaSource(readParam(params, 'utm_source'), null);
  if (!fromMeta) return null;
  const pick = (...values: Array<string | null>) => {
    for (const value of values) {
      const id = normalizeMetaAdObjectId(value);
      if (id) return id;
    }
    return null;
  };
  return {
    campaignId: pick(
      readParam(params, 'campaign_id', 'fb_campaign_id'),
      readParam(params, 'utm_id'),
      readParam(params, 'utm_campaign'),
    ),
    adsetId: pick(readParam(params, 'adset_id', 'fb_adset_id'), readParam(params, 'utm_term')),
    adId: pick(readParam(params, 'ad_id', 'fb_ad_id'), readParam(params, 'utm_content')),
  };
}

function buildFbc(fbclid: string | null, timestampIso: string): string | null {
  if (!fbclid) return null;
  const timestamp = Date.parse(timestampIso);
  const millis = Number.isFinite(timestamp) ? timestamp : Date.now();
  return `fb.1.${millis}.${fbclid}`;
}

function mergeValue(nextValue: string | null, fallback: string | null): string | null {
  return nextValue && nextValue.trim().length > 0 ? nextValue.trim() : fallback;
}

function compact(value?: string | null): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeReferrer(rawReferrer?: string | null): string | null {
  const referrer = compact(rawReferrer);
  if (!referrer) return null;

  try {
    const parsed = new URL(referrer);
    if (parsed.hostname === 'journalparty.com' || parsed.hostname.endsWith('.journalparty.com')) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

// Distinguishes "no referrer existed" from "referrer existed but was our own
// origin" so unattributed signups stay diagnosable instead of collapsing into
// one dark bucket.
export function classifyReferrerSource(rawReferrer?: string | null): ReferrerSource {
  const referrer = compact(rawReferrer);
  if (!referrer) return 'empty';
  return normalizeReferrer(referrer) ? 'external' : 'same_origin';
}

function classifyReferrer(referrer: string | null): {
  source: string | null;
  medium: string | null;
} {
  if (!referrer) return { source: null, medium: null };

  try {
    const { hostname } = new URL(referrer);
    const searchMatch = SEARCH_REFERRER_HOSTS.find(({ hostPattern }) => hostPattern.test(hostname));
    if (searchMatch) {
      return {
        source: searchMatch.source,
        medium: 'organic',
      };
    }

    return {
      source: hostname.replace(/^www\./i, ''),
      medium: 'referral',
    };
  } catch {
    return { source: null, medium: null };
  }
}

function readAppsFlyerField(payload: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  }
  return null;
}

/**
 * The query of the link inside an AppsFlyer deep link payload. A OneLink tap
 * can carry its source (pid, c, utm_*) only there, not as payload keys.
 */
export function getAppsFlyerLinkParams(payload: Record<string, unknown>): URLSearchParams | null {
  const data = payload.data && typeof payload.data === 'object' ? (payload.data as Record<string, unknown>) : {};
  const link = [payload.link, data.link].find((value): value is string => typeof value === 'string' && Boolean(value.trim()));
  if (!link) return null;
  try {
    return new URL(link.trim()).searchParams;
  } catch {
    return null;
  }
}

function flattenAppsFlyerPayload(payload: Record<string, unknown>): Record<string, string> {
  const flattened: Record<string, string> = {};
  const appendValue = (key: string, value: unknown) => {
    if (typeof value === 'string' && value.trim()) {
      flattened[key] = value.trim();
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      flattened[key] = String(value);
    }
  };

  for (const [key, value] of Object.entries(payload)) {
    if (key === 'data' && value && typeof value === 'object' && !Array.isArray(value)) {
      for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
        appendValue(nestedKey, nestedValue);
      }
    } else {
      appendValue(key, value);
    }
  }

  // Payload keys win; the link query only fills source params they lack.
  // Routing keys (deep_link_*, af_dp) stay payload-only so routing is unchanged.
  for (const [key, value] of getAppsFlyerLinkParams(payload) ?? []) {
    if (!(key in flattened) && LINK_SOURCE_PARAM_NAMES.has(key)) {
      appendValue(key, value);
    }
  }

  return flattened;
}

function normalizeKey(value?: string | null): string {
  return (
    compact(value)
      ?.toLowerCase()
      .replace(/[\s-]+/g, '_') || ''
  );
}

function isMetaSource(source?: string | null, network?: string | null): boolean {
  const normalizedSource = normalizeKey(source);
  const normalizedNetwork = normalizeKey(network);
  return META_SOURCE_VALUES.has(normalizedSource) || META_SOURCE_VALUES.has(normalizedNetwork);
}

function isPaidMedium(medium?: string | null): boolean {
  const normalized = compact(medium);
  return Boolean(normalized && PAID_MEDIUM_REGEX.test(normalized));
}

function isMetaPlacementMedium(medium?: string | null): boolean {
  const normalized = compact(medium);
  return Boolean(normalized && META_PLACEMENT_MEDIUM_REGEX.test(normalized));
}

export function normalizeAttributionForGa4(input: Ga4AttributionSource | null): NormalizedGa4Attribution {
  const source = compact(input?.utm_source || input?.network || null);
  const medium = compact(input?.utm_medium || null);
  const hasMetaSignal = isMetaSource(input?.utm_source, input?.network) || Boolean(compact(input?.fbclid));
  const shouldNormalizeMetaMedium =
    hasMetaSignal && !isPaidMedium(medium) && (Boolean(compact(input?.fbclid)) || isMetaPlacementMedium(medium));

  return {
    source: source || (shouldNormalizeMetaMedium ? 'meta' : null),
    medium: shouldNormalizeMetaMedium ? GA4_PAID_SOCIAL_MEDIUM : medium,
    campaign: compact(input?.utm_campaign || null),
    term: compact(input?.utm_term || null),
    content: compact(input?.utm_content || null),
    paidSocialPlacement: shouldNormalizeMetaMedium ? medium : null,
    wasNormalized: shouldNormalizeMetaMedium,
  };
}

export function normalizeUrlForGa4Attribution(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, 'https://journalparty.com');
    const normalized = normalizeAttributionForGa4({
      utm_source: url.searchParams.get('utm_source'),
      utm_medium: url.searchParams.get('utm_medium'),
      utm_campaign: url.searchParams.get('utm_campaign'),
      utm_term: url.searchParams.get('utm_term'),
      utm_content: url.searchParams.get('utm_content'),
      fbclid: url.searchParams.get('fbclid'),
    });

    if (!normalized.wasNormalized) return rawUrl;

    if (normalized.source && !url.searchParams.get('utm_source')) {
      url.searchParams.set('utm_source', normalized.source);
    }
    if (normalized.medium) {
      url.searchParams.set('utm_medium', normalized.medium);
    }

    return url.toString();
  } catch {
    return rawUrl;
  }
}

function createContext(url: URL, existing: AcquisitionContext | null, rawReferrer?: string | null): AcquisitionContext {
  const now = new Date().toISOString();
  const privacySafeUrl = new URL(sanitizeSensitiveUrl(url.toString(), 'absolute'));
  const params = privacySafeUrl.searchParams;
  // Only a matching touch may enrich missing fields. A new source, medium,
  // campaign or click resets the complete bundle, including absent fields.
  const identities: Array<[string | null, string | null | undefined]> = [
    [
      readParam(params, "utm_source", "media_source", "pid"),
      existing?.utm_source,
    ],
    [readParam(params, "utm_medium"), existing?.utm_medium],
    [
      readParam(params, "utm_campaign", "campaign", "c"),
      existing?.utm_campaign,
    ],
    ...(
      ["fbclid", "gclid", "gbraid", "wbraid", "ttclid", "msclkid"] as const
    ).map((key): [string | null, string | null | undefined] => [
      readParam(params, key),
      existing?.[key],
    ]),
  ];
  const conflictingIdentity = identities.some(
    ([next, previous]) => next && previous && next !== previous,
  );
  const sameClick = (
    ["fbclid", "gclid", "gbraid", "wbraid", "ttclid", "msclkid"] as const
  ).some(
    (key) =>
      readParam(params, key) && readParam(params, key) === existing?.[key],
  );
  const namesNewTouch = [
    "utm_source",
    "media_source",
    "pid",
    "utm_medium",
    "utm_campaign",
    "campaign",
    "c",
    "fbclid",
    "gclid",
    "gbraid",
    "wbraid",
    "ttclid",
    "msclkid",
  ].some((key) => params.has(key));
  if (
    conflictingIdentity ||
    (namesNewTouch &&
      existing?.source_url !== privacySafeUrl.toString() &&
      !sameClick)
  ) {
    existing = null;
  }
  const capturedAt = existing?.captured_at || now;
  const urlFbclid = readParam(params, 'fbclid');
  const fbclid = mergeValue(urlFbclid, existing?.fbclid || null);
  // A different fbclid is a new ad click: its _fbc carries this click's
  // time, and its campaign ids and utm_id replace the last click's (even
  // when absent).
  const isNewMetaClick = Boolean(urlFbclid && urlFbclid !== existing?.fbclid);
  const metaIds = readMetaAdObjectIds(params);
  const mergeMetaId = (next: string | null | undefined, previous: string | null | undefined) =>
    isNewMetaClick ? next ?? null : mergeValue(next ?? null, previous ?? null);

  return {
    captured_at: capturedAt,
    last_touched_at: now,
    source_url: privacySafeUrl.toString(),
    landing_path: `${privacySafeUrl.pathname}${privacySafeUrl.search}${privacySafeUrl.hash}` || null,
    referrer: existing?.referrer || normalizeReferrer(rawReferrer),
    referrer_source: existing?.referrer_source || classifyReferrerSource(rawReferrer),
    af_status:
      normalizeAppsFlyerAttributionStatus(readParam(params, 'af_status')) ||
      normalizeAppsFlyerAttributionStatus(existing?.af_status),
    network: mergeValue(inferNetwork(params), existing?.network || null),
    utm_source: mergeValue(readParam(params, 'utm_source', 'media_source', 'pid'), existing?.utm_source || null),
    utm_medium: mergeValue(readParam(params, 'utm_medium'), existing?.utm_medium || null),
    utm_campaign: mergeValue(readParam(params, 'utm_campaign', 'campaign', 'c'), existing?.utm_campaign || null),
    utm_term: mergeValue(readParam(params, 'utm_term', 'af_keywords'), existing?.utm_term || null),
    utm_content: mergeValue(readParam(params, 'utm_content'), existing?.utm_content || null),
    // Meta's URL builder puts the campaign id in utm_id, so a new Meta click
    // resets it with the Meta ids.
    utm_id: mergeMetaId(readParam(params, 'utm_id'), existing?.utm_id),
    gclid: mergeValue(readParam(params, 'gclid'), existing?.gclid || null),
    gbraid: mergeValue(readParam(params, 'gbraid'), existing?.gbraid || null),
    wbraid: mergeValue(readParam(params, 'wbraid'), existing?.wbraid || null),
    fbclid,
    ttclid: mergeValue(readParam(params, 'ttclid'), existing?.ttclid || null),
    msclkid: mergeValue(readParam(params, 'msclkid'), existing?.msclkid || null),
    campaign: mergeValue(readParam(params, 'campaign', 'campaign_id', 'c', 'af_c_id'), existing?.campaign || null),
    ad_group: mergeValue(
      readParam(params, 'adgroup', 'ad_group', 'adgroup_id', 'adset', 'adset_id', 'af_adset', 'af_adset_id'),
      existing?.ad_group || null,
    ),
    ad: mergeValue(readParam(params, 'ad', 'ad_id', 'af_ad', 'af_ad_id'), existing?.ad || null),
    creative: mergeValue(readParam(params, 'creative', 'creative_id'), existing?.creative || null),
    keyword: mergeValue(readParam(params, 'keyword', 'af_keywords'), existing?.keyword || null),
    fbc: isNewMetaClick
      ? buildFbc(urlFbclid, now)
      : existing?.fbc || buildFbc(fbclid, capturedAt),
    meta_campaign_id: mergeMetaId(metaIds?.campaignId, existing?.meta_campaign_id),
    meta_adset_id: mergeMetaId(metaIds?.adsetId, existing?.meta_adset_id),
    meta_ad_id: mergeMetaId(metaIds?.adId, existing?.meta_ad_id),
    appsflyer_media_source: mergeValue(
      readParam(params, 'media_source', 'pid'),
      existing?.appsflyer_media_source || null,
    ),
    appsflyer_campaign_id: mergeValue(readParam(params, 'af_c_id'), existing?.appsflyer_campaign_id || null),
    appsflyer_adset_id: mergeValue(readParam(params, 'af_adset_id'), existing?.appsflyer_adset_id || null),
    appsflyer_ad_id: mergeValue(readParam(params, 'af_ad_id'), existing?.appsflyer_ad_id || null),
    appsflyer_channel: mergeValue(readParam(params, 'af_channel'), existing?.appsflyer_channel || null),
    appsflyer_site_id: mergeValue(readParam(params, 'af_siteid'), existing?.appsflyer_site_id || null),
    deep_link_value: mergeValue(readParam(params, 'deep_link_value'), existing?.deep_link_value || null),
    deep_link_sub1: mergeValue(readParam(params, 'deep_link_sub1'), existing?.deep_link_sub1 || null),
    deep_link_sub2: mergeValue(readParam(params, 'deep_link_sub2'), existing?.deep_link_sub2 || null),
    deep_link_sub3: mergeValue(readParam(params, 'deep_link_sub3'), existing?.deep_link_sub3 || null),
    deep_link_sub4: mergeValue(readParam(params, 'deep_link_sub4'), existing?.deep_link_sub4 || null),
    deep_link_sub5: mergeValue(readParam(params, 'deep_link_sub5'), existing?.deep_link_sub5 || null),
  };
}

export function captureLandingAcquisition(urlString: string, rawReferrer?: string | null): AcquisitionContext | null {
  if (!urlString) return null;

  const existing = readStorage();

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(urlString);
  } catch {
    return null;
  }
  // Auth callback documents must never become the first touch: the landing
  // page would read /auth/callback and the referrer would read the OAuth
  // provider, which is self-referral pollution, not acquisition.
  if (isSensitiveAuthTelemetryUrl(parsedUrl.toString())) return null;
  parsedUrl = new URL(sanitizeSensitiveUrl(parsedUrl.toString(), 'absolute'));

  const now = new Date().toISOString();
  const referrer = normalizeReferrer(rawReferrer);
  const referrerClassification = classifyReferrer(referrer);

  const landing: AcquisitionContext = hasTrackingParams(parsedUrl.searchParams)
    ? createContext(parsedUrl, null, rawReferrer)
    : {
    captured_at: now,
    last_touched_at: now,
    source_url: parsedUrl.toString(),
    landing_path: `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}` || null,
    referrer,
    referrer_source: classifyReferrerSource(rawReferrer),
    af_status: null,
    network: referrerClassification.source,
    utm_source: referrerClassification.source,
    utm_medium: referrerClassification.medium,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    gclid: null,
    gbraid: null,
    wbraid: null,
    fbclid: null,
    ttclid: null,
    msclkid: null,
    campaign: null,
    ad_group: null,
    ad: null,
    creative: null,
    keyword: null,
    fbc: null,
  };
  storeTouchSnapshots(landing);
  return existing || writeStorage(landing);
}

export function captureLandingAcquisitionFromBrowser(): AcquisitionContext | null {
  if (typeof window === 'undefined') return null;
  return captureLandingAcquisition(window.location.href, typeof document === 'undefined' ? null : document.referrer);
}

/** Resolve current URL attribution without persisting a new touch. */
export function resolveAcquisitionFromUrl(
  urlString: string,
  rawReferrer?: string | null,
): AcquisitionContext | null {
  const existing = readStorage();
  try {
    const parsed = new URL(urlString);
    if (isSensitiveAuthTelemetryUrl(parsed.toString())) return existing;
    const safeUrl = new URL(
      sanitizeSensitiveUrl(parsed.toString(), "absolute"),
    );
    return hasTrackingParams(safeUrl.searchParams)
      ? createContext(safeUrl, existing, rawReferrer)
      : existing;
  } catch {
    return existing;
  }
}

export function captureAcquisitionFromUrl(urlString: string, rawReferrer?: string | null): AcquisitionContext | null {
  if (!urlString) return null;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(urlString);
  } catch {
    return null;
  }

  if (isSensitiveAuthTelemetryUrl(parsedUrl.toString())) {
    return readStorage();
  }
  parsedUrl = new URL(sanitizeSensitiveUrl(parsedUrl.toString(), 'absolute'));

  if (!hasTrackingParams(parsedUrl.searchParams)) {
    return null;
  }

  return writeStorage(createContext(parsedUrl, readStorage(), rawReferrer));
}

function createEmptyContext(now: string): AcquisitionContext {
  return {
    captured_at: now,
    last_touched_at: now,
    source_url: null,
    landing_path: null,
    referrer: null,
    referrer_source: null,
    af_status: null,
    network: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_term: null,
    utm_content: null,
    gclid: null,
    gbraid: null,
    wbraid: null,
    fbclid: null,
    ttclid: null,
    msclkid: null,
    campaign: null,
    ad_group: null,
    ad: null,
    creative: null,
    keyword: null,
    fbc: null,
  };
}

export function captureAcquisitionFromAppsFlyerPayload(
  payload: Record<string, unknown> | null | undefined,
): AcquisitionContext | null {
  if (!payload) return null;

  const flattened = flattenAppsFlyerPayload(payload);
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(flattened)) {
    params.set(key, value);
  }

  if (!hasTrackingParams(params)) {
    // An organic/privacy-restricted install carries no tracking params, but
    // af_status itself is signal: it separates "AppsFlyer said organic" from
    // "attribution never arrived" in user_acquisition.
    const afStatus =
      normalizeAppsFlyerAttributionStatus(flattened['af_status']) ||
      normalizeAppsFlyerAttributionStatus(flattened['status']);
    if (!afStatus) return null;

    const existing = readStorage();
    const now = new Date().toISOString();
    const existingAfStatus = normalizeAppsFlyerAttributionStatus(existing?.af_status);
    return writeStorage(
      existing
        ? {
            ...existing,
            af_status: existingAfStatus || afStatus,
            last_touched_at: now,
          }
        : { ...createEmptyContext(now), af_status: afStatus },
    );
  }

  const url = new URL('appsflyer://native-attribution');
  url.search = params.toString();
  return writeStorage(createContext(url, readStorage()));
}

function normalizeDeepLinkPath(value: string | null): string | null {
  const normalized = compact(value);
  if (!normalized) return null;

  try {
    const parsed = new URL(normalized, normalized.startsWith('/') ? 'https://journalparty.com' : undefined);
    if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
      if (!['journalparty.com', 'www.journalparty.com'].includes(parsed.hostname)) {
        return null;
      }
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
    if (parsed.protocol === 'journalparty:') {
      const pathFromQuery = compact(parsed.searchParams.get('path') || parsed.searchParams.get('route'));
      if (pathFromQuery) return normalizeDeepLinkPath(pathFromQuery);
      if (parsed.hostname && parsed.hostname !== 'auth') {
        return `${parsed.hostname.startsWith('/') ? '' : '/'}${parsed.hostname}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
    }
    return null;
  } catch {
    if (normalized.startsWith('/')) return normalized;
    if (/^[a-z0-9][a-z0-9-_/]*$/i.test(normalized)) {
      return `/${normalized}`;
    }
    return null;
  }
}

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function getDefaultShortShareMedium(pathname: string): string | null {
  if (pathname.startsWith('/p/')) return 'program_share';
  if (pathname.startsWith('/e/')) return 'event_share';
  if (pathname.startsWith('/j/')) return 'journey_share';
  if (isPromptPagePath(pathname)) return 'prompt_share';
  return null;
}

function normalizePublicNextPath(value: string | null): string | null {
  const trimmed = compact(value);
  if (!trimmed || !trimmed.startsWith('/') || trimmed.startsWith('//') || hasDotPathSegment(trimmed)) return null;

  const path = trimmed.split('?')[0] || trimmed;
  if (isPromptPagePath(path.split('#')[0])) return trimmed;

  if (
    path.startsWith('/p/') ||
    path.startsWith('/e/') ||
    path.startsWith('/j/') ||
    path.startsWith('/event/') ||
    path.startsWith('/events/') ||
    path.startsWith('/program/') ||
    path === '/programs' ||
    path === '/live'
  ) {
    return trimmed;
  }

  return null;
}

/**
 * Expands a /r/ or /ref/ short link into its full path with UTM params, or
 * null when it has no safe destination. Any other path comes back unchanged.
 */
export function expandShortSharePath(path: string): string | null {
  const parsed = new URL(path, 'https://journalparty.com');
  const segments = parsed.pathname.split('/').filter(Boolean);
  const [prefix, kind, slugOrId, ...extraSegments] = segments;

  if (!prefix || !SHORT_SHARE_PREFIXES.has(prefix)) return path;

  // /r/home, /r/live and /r/app, with the same rules as api/ssr.ts. /r/app
  // returns the absolute App Store URL.
  const fixedKind = getFixedShortLinkKind(kind, slugOrId ? 1 + extraSegments.length : 0);
  if (fixedKind === APP_SHORT_LINK_KIND) {
    return buildAppStoreShortLinkUrl(getShortShareParam(parsed, 's', 'utm_source'));
  }
  const fixedPath = getFixedShortLinkPath(fixedKind);
  if (fixedPath) return appendShortShareParams(fixedPath, parsed, FIXED_SHORT_LINK_MEDIUM);

  const nextPath = normalizePublicNextPath(parsed.searchParams.get('next'));
  if (nextPath) {
    return appendShortShareParams(nextPath, parsed);
  }

  // /r/jp/<page> opens /journal-prompts/<page>, with the same rules as api/ssr.ts.
  if (kind === PROMPT_SHORT_SHARE_KIND) {
    const promptPath = extraSegments.length > 0 ? null : buildPromptPageTargetPath(slugOrId);
    return promptPath ? appendShortShareParams(promptPath, parsed) : null;
  }

  if (!kind || !slugOrId || !SHORT_SHARE_KINDS.has(kind)) return null;

  return appendShortShareParams(`/${kind}/${encodeURIComponent(decodePathSegment(slugOrId))}`, parsed);
}

function getShortShareParam(url: URL, shortName: string, longName: string): string | null {
  return url.searchParams.get(shortName) || url.searchParams.get(longName);
}

function appendShortShareParams(targetPath: string, sourceUrl: URL, defaultMediumOverride?: string): string {
  const targetUrl = new URL(targetPath, 'https://journalparty.com');
  const source = getShortShareParam(sourceUrl, 's', 'utm_source');
  const explicitMedium = getShortShareParam(sourceUrl, 'm', 'utm_medium');
  const explicitCampaign = getShortShareParam(sourceUrl, 'c', 'utm_campaign');
  const term = getShortShareParam(sourceUrl, 't', 'utm_term');
  const content = getShortShareParam(sourceUrl, 'ct', 'utm_content');
  const defaultMedium = defaultMediumOverride ?? getDefaultShortShareMedium(targetUrl.pathname);
  const medium = explicitMedium || (source ? defaultMedium : null);
  const campaign = explicitCampaign || (source ? 'organic' : null);

  if (source) targetUrl.searchParams.set('utm_source', source);
  if (medium) targetUrl.searchParams.set('utm_medium', medium);
  if (campaign) targetUrl.searchParams.set('utm_campaign', campaign);
  if (term) targetUrl.searchParams.set('utm_term', term);
  if (content) targetUrl.searchParams.set('utm_content', content);

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
}

function isAllowedDeepLinkPath(path: string | null): path is string {
  if (!path || !path.startsWith('/')) return false;
  if (path.includes('/auth/native-callback') || path.includes('://auth/callback')) return false;

  return [
    '/',
    '/challenge',
    '/journal-party-live',
    '/programs',
    '/program/',
    '/p/',
    '/j/',
    '/journey/',
    // Web-only Journey funnels. The link is handed to the router on purpose:
    // NativeRouteFence sends it to "/", so no funnel UI ever mounts on native
    // and the app still opens instead of ignoring the tap.
    '/journal/',
    '/event/',
    '/events/',
    '/r/',
    '/ref/',
    '/share/program/',
    '/share/event/',
    '/share/journey/',
    '/pricing',
    '/dashboard',
    '/live',
    '/free',
    '/welcome-premium',
    '/first-session',
    '/daily-prompt/',
    // Native save-offer rescue: /settings?rescue=annual from push/email.
    '/settings',
  ].some((prefix) => path === prefix || (prefix !== '/' && path.startsWith(prefix)));
}

export function resolveNativeDeepLinkPath(url: string | null | undefined): string | null {
  const path = normalizeDeepLinkPath(url ?? null);
  if (!path) return null;

  // /journeys is a web alias (vercel.json 301); open the Journey list directly.
  const alias = /^\/journeys\/?(?:\?([^#]*))?(#.*)?$/.exec(path);
  const aliasedPath = alias
    ? `/programs?tab=journeys${alias[1] ? `&${alias[1]}` : ''}${alias[2] ?? ''}`
    : path;
  const expandedPath = expandShortSharePath(aliasedPath);
  if (!isAllowedDeepLinkPath(expandedPath)) return null;

  return expandedPath;
}

export function resolveAppsFlyerDeepLinkPath(payload: Record<string, unknown> | null | undefined): string | null {
  if (!payload) return null;

  const flattened = flattenAppsFlyerPayload(payload);
  const candidates = [
    readAppsFlyerField(flattened, 'deep_link_value'),
    readAppsFlyerField(flattened, 'deep_link_sub1'),
    readAppsFlyerField(flattened, 'deep_link_sub2'),
    readAppsFlyerField(flattened, 'af_dp'),
  ];

  for (const candidate of candidates) {
    const path = resolveNativeDeepLinkPath(candidate);
    if (path) return path;
  }

  return null;
}

/** First observed touch since this contract was introduced; legacy history stays unknown. */
export function getFirstTouchAcquisitionContext(): AcquisitionContext | null {
  return readStorage(FIRST_TOUCH_KEY);
}

/** Entry touch for this tab; expires after 30 minutes without a capture (route/landing activity). */
export function getSessionAcquisitionContext(): AcquisitionContext | null {
  return readStorage(SESSION_TOUCH_KEY, true);
}

/** Compatibility accessor for the latest coherent captured touch. */
export function getStoredAcquisitionContext(): AcquisitionContext | null {
  return readStorage();
}

export function getAttributionUtmParams():
  | {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
    }
  | undefined {
  const context = getStoredAcquisitionContext();
  if (!context) return undefined;

  return {
    utm_source: context.utm_source || undefined,
    utm_medium: context.utm_medium || undefined,
    utm_campaign: context.utm_campaign || undefined,
    utm_term: context.utm_term || undefined,
    utm_content: context.utm_content || undefined,
  };
}

/** utm_id, fbclid and the latest Meta ad click's campaign, ad set and ad ids. */
export function getAttributionAdClickIds(): {
  utm_id?: string;
  fbclid?: string;
  meta_campaign_id?: string;
  meta_adset_id?: string;
  meta_ad_id?: string;
} {
  const context = getStoredAcquisitionContext();
  if (!context) return {};
  return {
    utm_id: context.utm_id || undefined,
    fbclid: context.fbclid || undefined,
    meta_campaign_id: context.meta_campaign_id || undefined,
    meta_adset_id: context.meta_adset_id || undefined,
    meta_ad_id: context.meta_ad_id || undefined,
  };
}

export function getAttributionIdentifiers(): {
  fbc?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  ttclid?: string;
} {
  const context = getStoredAcquisitionContext();
  const cookieTtclid = readCookie(TIKTOK_CLICK_ID_COOKIE) || undefined;
  if (!context) {
    return {
      ttclid: cookieTtclid,
    };
  }

  return {
    fbc: context.fbc || undefined,
    gclid: context.gclid || undefined,
    gbraid: context.gbraid || undefined,
    wbraid: context.wbraid || undefined,
    // A cookie from an earlier TikTok touch cannot join a later campaign.
    ttclid: context.ttclid || undefined,
  };
}

export function getGa4AttributionParams(context: Ga4AttributionSource | null = getStoredAcquisitionContext()): {
  acquisition_source?: string;
  acquisition_medium?: string;
  acquisition_campaign?: string;
  acquisition_keyword?: string;
  acquisition_creative?: string;
  paid_social_placement?: string;
} {
  const normalized = normalizeAttributionForGa4(context);

  return {
    acquisition_source: normalized.source || undefined,
    acquisition_medium: normalized.medium || undefined,
    acquisition_campaign: normalized.campaign || undefined,
    acquisition_keyword: normalized.term || undefined,
    acquisition_creative: normalized.content || undefined,
    paid_social_placement: normalized.paidSocialPlacement || undefined,
  };
}

export function getRevenueCatAttributionContext(): RevenueCatAttributionContext | null {
  const context = getStoredAcquisitionContext();
  if (!context) return null;

  // RevenueCat files a customer with no $mediaSource under "No Attribution",
  // which hid the ~85% of installs AppsFlyer answers as organic. A tracked
  // source still wins; a non-organic answer without a source stays empty.
  const appsFlyerOrganic =
    normalizeAppsFlyerAttributionStatus(context.af_status) === 'Organic' ? 'organic' : null;

  return {
    mediaSource: context.utm_source || context.network || appsFlyerOrganic,
    campaign: context.utm_campaign || context.campaign || null,
    adGroup: context.ad_group || null,
    ad: context.ad || null,
    creative: context.creative || context.utm_content || null,
    keyword: context.keyword || context.utm_term || null,
  };
}
